const base = process.env.PUBLIC_URL || "";

export const READING_GUIDES = [
  {
    slug: "calm-reading-routine",
    tag: "Habits",
    title: "A Calm Reading Routine",
    author: "PagePeers Editors",
    readTime: "7 min read",
    excerpt:
      "Build a sustainable rhythm that fits real life — short sessions, gentle goals, and no streak anxiety.",
    cover: `${base}/images/quiet-shelf/morning.jpg`,
    topics: ["Habits", "Mindset", "Beginners"],
  },
  {
    slug: "first-reader-circle",
    tag: "Community",
    title: "Joining Your First Reader Circle",
    author: "Mira Holloway",
    readTime: "6 min read",
    excerpt:
      "How to browse open circles, read the welcome note, and show up without feeling behind.",
    cover: `${base}/images/reader-circles/library.jpg`,
    topics: ["Circles", "Community", "Social"],
  },
  {
    slug: "organize-your-shelf",
    tag: "Shelf",
    title: "Organize Your Shelf Without Overwhelm",
    author: "PagePeers Editors",
    readTime: "8 min read",
    excerpt:
      "Start small, use lists that match how you think, and let metadata arrive when you are ready.",
    cover: `${base}/images/reader-circles/stacks.jpg`,
    topics: ["Shelf", "Organization", "Tips"],
  },
  {
    slug: "spoiler-safe-discussions",
    tag: "Circles",
    title: "Spoiler-Safe Discussions",
    author: "PagePeers Club",
    readTime: "5 min read",
    excerpt:
      "Chapter lanes, pinned norms, and kind replies so late joiners still feel welcome.",
    cover: `${base}/images/reader-circles/card-cover.jpg`,
    topics: ["Circles", "Etiquette", "Discussion"],
  },
  {
    slug: "choose-your-next-book",
    tag: "Discovery",
    title: "Choosing Your Next Book",
    author: "Arjun Mehra",
    readTime: "6 min read",
    excerpt:
      "When the TBR pile feels infinite — mood filters, circle picks, and the one-book rule.",
    cover: `${base}/images/weekend-escape/open-book.jpg`,
    topics: ["Discovery", "TBR", "Mood"],
  },
  {
    slug: "seasonal-reading",
    tag: "Seasons",
    title: "Reading with the Seasons",
    author: "Priya Rao",
    readTime: "7 min read",
    excerpt:
      "Match books to weather, energy, and the pace of your calendar — not someone else's list.",
    cover: `${base}/images/rainy-day-reads/hero.jpg`,
    topics: ["Seasons", "Curated", "Comfort"],
  },
];

export const READING_GUIDE_ARTICLES = {
  "calm-reading-routine": {
    lede: "Reading should feel like permission, not homework.",
    intro:
      "Most of us were taught to finish fast and report back. PagePeers is built on a different idea: small, repeatable rituals that make returning to a book easier than avoiding it.",
    sections: [
      {
        heading: "Anchor a time, not a quota",
        body: "Pick a window you already have — tea after work, twenty minutes before sleep, Sunday morning. Consistency of place and time matters more than page count. When the bell rings, you read; when life interrupts, you simply pick up tomorrow.",
      },
      {
        heading: "Keep one book in the open",
        body: "Leave your current read on the nightstand or desk. Visibility beats willpower. If you read digitally, pin the title to your home screen or shelf's \"reading now\" list so it is the first thing you see.",
      },
      {
        heading: "End mid-chapter on purpose",
        body: "Stopping before a natural break creates gentle tension that pulls you back. Ernest Hemingway called this leaving water in the well — you always know where to resume.",
      },
    ],
    tips: [
      "Mute non-essential notifications during your reading window.",
      "Log status in PagePeers when you pause — future you will thank you.",
      "Share one line in a circle if you want company without a book report.",
    ],
    closing:
      "A calm routine compounds. Six weeks of ten-minute sessions often finishes more books than two heroic weekends that burn you out.",
  },
  "first-reader-circle": {
    lede: "Circles are kitchens, not stadiums.",
    intro:
      "Reader circles on PagePeers are small groups trading highlights, questions, and recommendations. You do not need a finished book or a hot take to belong.",
    sections: [
      {
        heading: "Read the welcome note first",
        body: "Every circle pins expectations: pace, spoiler rules, and tone. If the vibe matches — literary, cozy, ambitious — join. If not, browse another. There is no penalty for lurking a day before you post.",
      },
      {
        heading: "Introduce yourself lightly",
        body: "One sentence about what you are reading and one book you loved recently is enough. Circles reward generosity over credentials.",
      },
      {
        heading: "Reply before you perform",
        body: "Comment on someone else's highlight before posting your own manifesto. Conversation grows from response, not broadcast.",
      },
    ],
    tips: [
      "Start with an \"Open\" or \"New\" tagged circle if you want a gentler pace.",
      "Use chapter lanes when the group is mid-book.",
      "Save shelf picks from the circle to your wishlist in one tap.",
    ],
    closing:
      "The best circles feel like a friend left the porch light on. Show up as you are; the right group will meet you there.",
  },
  "organize-your-shelf": {
    lede: "Your shelf is a desk, not a database.",
    intro:
      "PagePeers lets you grow organization over time. The goal is to find the next book quickly, not to catalog every edition on day one.",
    sections: [
      {
        heading: "Three lists are enough to start",
        body: "Reading now, up next, and finished cover most real life. Add favorites or lent out when you need them — not before.",
      },
      {
        heading: "Tag when it helps you decide",
        body: "Genre, mood, or format tags shine when you are choosing what to read next. Skip tags for books you have already finished unless you enjoy the archaeology.",
      },
      {
        heading: "Let the shelf reflect seasons",
        body: "Move winter cozies off \"up next\" in spring. A living shelf changes with you; perfection is the enemy of use.",
      },
    ],
    tips: [
      "Bulk-add from a public shelf you follow.",
      "Drag to reorder on desktop; your order is yours alone.",
      "Keep lent books visible so they find their way home.",
    ],
    closing:
      "Organization serves reading. The moment upkeep feels like work, simplify until it feels like care again.",
  },
  "spoiler-safe-discussions": {
    lede: "Trust is the infrastructure of good talk about books.",
    intro:
      "Spoilers are not malicious — they are mistimed. A few shared habits keep circles welcoming for readers at every chapter.",
    sections: [
      {
        heading: "Use chapter lanes",
        body: "Post reactions in the lane that matches how far you have read. Late joiners can scroll without fear.",
      },
      {
        heading: "Lead with scope",
        body: "Start posts with \"through chapter 12\" or \"finale thoughts inside.\" Headers save more friendships than apologies.",
      },
      {
        heading: "Assume someone is on page one",
        body: "The newest member is always arriving. Write for them first; veterans will find the depth they want.",
      },
    ],
    tips: [
      "Pin spoiler norms in the circle welcome note.",
      "Use quotes instead of plot summary when you can.",
      "React with warmth, not one-upmanship.",
    ],
    closing:
      "When people feel safe from spoilers, they read slower and talk better — exactly what a circle is for.",
  },
  "choose-your-next-book": {
    lede: "Decision fatigue is the enemy of the TBR.",
    intro:
      "Too many good options freeze us. These filters help you pick one book tonight without guilt about the rest.",
    sections: [
      {
        heading: "Name the mood, not the masterpiece",
        body: "Ask: Do I want comfort, challenge, escape, or company? Mood beats merit when you are tired.",
      },
      {
        heading: "Borrow a circle's pick",
        body: "If a thread is glowing, read what sparked it. Shared context makes the first fifty pages easier.",
      },
      {
        heading: "Apply the one-book rule",
        body: "Choose one title for this week. Everything else waits on \"up next\" without shame. You can change your mind Monday.",
      },
    ],
    tips: [
      "Browse shelves tagged for your current season.",
      "Re-read a favorite if nothing new feels right — that counts.",
      "Log DNFs without drama; they clarify taste.",
    ],
    closing:
      "The right next book is the one you will actually open tonight. PagePeers is here to make that choice smaller and kinder.",
  },
  "seasonal-reading": {
    lede: "Let weather and light steer the stack.",
    intro:
      "Seasonal reading is not a trend list — it is matching energy. Grey skies invite atmosphere; bright weeks invite propulsion.",
    sections: [
      {
        heading: "Build a small seasonal shelf",
        body: "Keep six to ten titles tagged for the current season on PagePeers. When mood shifts, you are not starting from zero.",
      },
      {
        heading: "Rotate displays at home",
        body: "Face-out books on a windowsill or tray change what your eyes reach for. Digital shelves can mirror the same rotation.",
      },
      {
        heading: "Pair reading with ritual",
        body: "Rainy-day tea, summer porch light, autumn blanket — sensory cues make seasonal picks feel inevitable instead of forced.",
      },
    ],
    tips: [
      "Follow the Rainy Day Reads and Weekend Escape shelves for inspiration.",
      "Share seasonal swaps in your circle for accountability.",
      "Note finish dates to see patterns year over year.",
    ],
    closing:
      "Seasonal reading honors the body you read in, not just the mind. Trust the weather; the right book often arrives with it.",
  },
};
